import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  ComplianceFramework,
  EvidenceApproval,
  EvidenceItem,
  EvidenceLink,
  EvidenceLinkRole,
  EvidenceLinkTarget,
  EvidenceVersion,
  FrameworkRequirement,
  FrameworkVersion,
  Policy,
  PolicyDocument,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, documentsApi, evidenceApi, frameworksApi, policyApi } from "../../lib/api";
import { DocumentSelect } from "../../components/DocumentsPicker";
import {
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import {
  EVIDENCE_LINK_ROLES,
  EVIDENCE_LINK_TARGETS,
  EvidenceStatusBadge,
  LinkRoleBadge,
  SensitivityBadge,
  sourceLabel,
  targetLabel,
} from "./evidenceLabels";

// One evidence item: its immutable versions, its links, its decision history and
// the governed lifecycle actions (APP-004 R3 P2).
//
// Statuses, content hashes and segregation-of-duties refusals all come from the
// backend. Only transitions the backend can accept from the current status are
// offered, and a refusal is shown with its own wording.
export function GovEvidenceItem() {
  const { itemId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";

  const detail = useAsync(async () => (await evidenceApi.item(orgId, itemId)).data, [orgId, itemId]);
  const history = useAsync(
    async () => (await evidenceApi.history(orgId, itemId)).data.history,
    [orgId, itemId],
  );
  const members = useAsync(async () => (await api.members(orgId)).members, [orgId]);
  const siblings = useAsync(async () => (await evidenceApi.items(orgId)).data.items, [orgId]);

  const memberName = (userId: string) => {
    const match = (members.data ?? []).find((m) => m.id === userId);
    return match ? match.displayName || match.email : userId;
  };
  const reload = () => {
    detail.reload();
    history.reload();
  };

  return (
    <GovPanel bare state={detail} loadingLabel="Loading evidence…">
      {({ item, versions, links }) => {
        const current = versions.find((v) => v.id === item.currentVersionId) ?? null;
        return (
          <>
            <div className="page-head">
              <div>
                <h1>{item.title}</h1>
                <p>
                  <Link to="/studio/governance/evidence">Evidence</Link> · {sourceLabel(item.source)} ·
                  owned by {memberName(item.ownerId)}
                </p>
                <div className="badge-row">
                  <EvidenceStatusBadge status={item.status} />
                  <SensitivityBadge sensitivity={item.sensitivity} />
                  <Badge tone="neutral">{item.evidenceType}</Badge>
                </div>
              </div>
              <div className="badge-row">
                <Lifecycle
                  orgId={orgId}
                  item={item}
                  replacements={(siblings.data ?? []).filter((i) => i.id !== item.id)}
                  onDone={reload}
                />
              </div>
            </div>

            <Card>
              <CardHeader title="Evidence detail" />
              <CardBody>
                <dl className="detail-grid">
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <EvidenceStatusBadge status={item.status} />
                    </dd>
                  </div>
                  <div>
                    <dt>Current version</dt>
                    <dd>{current ? `v${current.versionNumber}` : "None captured yet"}</dd>
                  </div>
                  <div>
                    <dt>Captured by</dt>
                    <dd>{current ? memberName(current.capturedBy) : "Nobody yet"}</dd>
                  </div>
                  <div>
                    <dt>Content hash</dt>
                    <dd>{current ? <code>{current.contentHash}</code> : "None"}</dd>
                  </div>
                  <div>
                    <dt>Source</dt>
                    <dd>{sourceLabel(item.source)}</dd>
                  </div>
                  <div>
                    <dt>Sensitivity</dt>
                    <dd>
                      <SensitivityBadge sensitivity={item.sensitivity} />
                    </dd>
                  </div>
                  <div>
                    <dt>Effective dates</dt>
                    <dd>
                      {formatDate(item.effectiveStart)} to {formatDate(item.effectiveEnd)}
                    </dd>
                  </div>
                  <div>
                    <dt>Review date</dt>
                    <dd>{formatDate(item.reviewAt)}</dd>
                  </div>
                  <div>
                    <dt>Owner</dt>
                    <dd>{memberName(item.ownerId)}</dd>
                  </div>
                  <div>
                    <dt>Created by</dt>
                    <dd>
                      {memberName(item.createdBy)} · {formatDate(item.createdAt)}
                    </dd>
                  </div>
                  {item.supersededById ? (
                    <div>
                      <dt>Superseded by</dt>
                      <dd>
                        <Link to={`/studio/governance/evidence/items/${item.supersededById}`}>
                          {(siblings.data ?? []).find((i) => i.id === item.supersededById)?.title ??
                            item.supersededById}
                        </Link>
                      </dd>
                    </div>
                  ) : null}
                </dl>
                <p className="muted-note">
                  Approval is refused when the backend finds a segregation of duties conflict. The owner,
                  the creator, whoever captured the current version and whoever submitted it cannot approve
                  their own evidence.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Versions"
                action={
                  <AddVersion orgId={orgId} item={item} onDone={reload} />
                }
              />
              <CardBody>
                {versions.length === 0 ? (
                  <p className="muted-note">
                    No content captured yet. Evidence cannot be submitted until a version exists.
                  </p>
                ) : (
                  <DataTable
                    caption="Evidence versions"
                    columns={[
                      {
                        key: "version",
                        header: "Version",
                        render: (row) => (
                          <>
                            <strong>v{row.versionNumber}</strong>
                            {row.id === item.currentVersionId ? (
                              <div className="badge-row">
                                <Badge tone="info">Current</Badge>
                              </div>
                            ) : null}
                          </>
                        ),
                      },
                      {
                        key: "content",
                        header: "Content",
                        render: (row) =>
                          row.documentId ? (
                            <>
                              Documents record <code>{row.documentId}</code>
                            </>
                          ) : row.externalUrl ? (
                            <a href={row.externalUrl} rel="noreferrer noopener" target="_blank">
                              {row.externalUrl}
                            </a>
                          ) : (
                            row.attestationText
                          ),
                      },
                      { key: "hash", header: "Content hash", render: (row) => <code>{row.contentHash}</code> },
                      {
                        key: "captured",
                        header: "Captured",
                        render: (row) => (
                          <>
                            {memberName(row.capturedBy)}
                            <div className="muted-note">{formatDate(row.capturedAt)}</div>
                          </>
                        ),
                      },
                    ]}
                    rows={versions as (EvidenceVersion & { id: string })[]}
                  />
                )}
                <p className="muted-note">
                  Versions are immutable and their hashes are computed by Policy AI. Capturing new content on
                  approved evidence returns it to draft, so an approval always refers to what was approved.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Linked targets"
                action={<AddLink orgId={orgId} itemId={item.id} onDone={reload} />}
              />
              <CardBody>
                {links.length === 0 ? (
                  <p className="muted-note">
                    Not linked to anything yet. Only approved, evidence inside its window linked as
                    <strong> supports</strong> can close a requirement.
                  </p>
                ) : (
                  <DataTable
                    caption="Evidence links"
                    columns={[
                      {
                        key: "target",
                        header: "Target",
                        render: (row) => (
                          <>
                            <strong>{targetLabel(row.targetType)}</strong>
                            <div className="muted-note">
                              <code>{row.targetId}</code>
                            </div>
                          </>
                        ),
                      },
                      { key: "role", header: "Role", render: (row) => <LinkRoleBadge role={row.linkRole} /> },
                      { key: "created", header: "Linked", render: (row) => formatDate(row.createdAt) },
                      {
                        key: "actions",
                        header: "",
                        render: (row) => (
                          <GovernedAction
                            permission={GOVERNANCE_PERMISSIONS.evidenceManage}
                            label="Remove"
                            title="Remove this link"
                            description={`Unlinks this evidence from ${targetLabel(row.targetType)} ${row.targetId}.`}
                            consequence="Removing a supporting link can leave the target with no evidence, which the next health read will report as missing."
                            confirmLabel="Remove link"
                            busyLabel="Removing…"
                            destructive
                            onConfirm={() => evidenceApi.removeLink(orgId, item.id, row.id)}
                            onDone={reload}
                          />
                        ),
                      },
                    ]}
                    rows={links as (EvidenceLink & { id: string })[]}
                  />
                )}
              </CardBody>
            </Card>

            <GovPanel
              title="Decision history"
              state={history}
              loadingLabel="Loading history…"
              isEmpty={(rows) => rows.length === 0}
              emptyTitle="No decisions yet"
              emptyDescription="Submitting, approving, rejecting, reopening, superseding and expiring are all recorded here."
            >
              {(rows) => (
                <>
                  <DataTable
                    caption="Evidence decision history"
                    columns={[
                      { key: "decision", header: "Decision", render: (row) => <Badge>{row.decision}</Badge> },
                      {
                        key: "transition",
                        header: "Status",
                        render: (row) => `${row.fromStatus} → ${row.toStatus}`,
                      },
                      { key: "actor", header: "Actor", render: (row) => memberName(row.actorId) },
                      { key: "rationale", header: "Rationale", render: (row) => row.rationale || "None recorded" },
                      { key: "at", header: "Decided", render: (row) => formatDate(row.decidedAt) },
                    ]}
                    rows={rows as (EvidenceApproval & { id: string })[]}
                  />
                  <p className="muted-note">
                    Append only: a later decision never edits an earlier record.
                  </p>
                </>
              )}
            </GovPanel>
          </>
        );
      }}
    </GovPanel>
  );
}

/**
 * Only the transitions the backend accepts from this status are offered. The
 * backend still decides — a refusal (including a segregation-of-duties 409) is
 * shown with its own message.
 */
function Lifecycle({
  orgId,
  item,
  replacements,
  onDone,
}: {
  orgId: string;
  item: EvidenceItem;
  replacements: EvidenceItem[];
  onDone: () => void;
}) {
  const [replacementId, setReplacementId] = useState("");
  const replacement = replacementId || replacements[0]?.id || "";

  const actions = [];
  if (item.status === "draft") {
    actions.push(
      <GovernedAction
        key="submit"
        permission={GOVERNANCE_PERMISSIONS.evidenceManage}
        label="Submit"
        title="Submit this evidence for approval"
        description="Freezes the current version as what is being submitted for review."
        consequence="Submitting requires a captured version. Whoever submits cannot also approve."
        confirmLabel="Submit"
        busyLabel="Submitting…"
        triggerVariant="primary"
        onConfirm={(rationale) => evidenceApi.submitItem(orgId, item.id, rationale)}
        onDone={onDone}
      />,
    );
  }
  if (item.status === "submitted") {
    actions.push(
      <GovernedAction
        key="approve"
        permission={GOVERNANCE_PERMISSIONS.evidenceApprove}
        label="Approve"
        title="Approve this evidence"
        description="Records that this evidence is acceptable as it stands."
        consequence="Approved, evidence inside its window is what counts towards coverage and what a package freezes. Policy AI refuses the approval if you produced this evidence yourself."
        confirmLabel="Approve evidence"
        busyLabel="Approving…"
        requireRationale
        rationaleHelp="What you checked. Stored with the decision."
        triggerVariant="primary"
        onConfirm={(rationale) => evidenceApi.approveItem(orgId, item.id, rationale)}
        onDone={onDone}
      />,
      <GovernedAction
        key="reject"
        permission={GOVERNANCE_PERMISSIONS.evidenceApprove}
        label="Reject"
        title="Reject this evidence"
        description="Records that this evidence is not acceptable."
        consequence="Rejected evidence counts towards nothing. It can be reopened and revised."
        confirmLabel="Reject evidence"
        busyLabel="Rejecting…"
        requireRationale
        destructive
        onConfirm={(rationale) => evidenceApi.rejectItem(orgId, item.id, rationale)}
        onDone={onDone}
      />,
    );
  }
  if (item.status === "rejected" || item.status === "expired") {
    actions.push(
      <GovernedAction
        key="reopen"
        permission={GOVERNANCE_PERMISSIONS.evidenceManage}
        label="Reopen"
        title="Reopen this evidence"
        description="Returns the evidence to draft so it can be revised and submitted again."
        consequence="A reopened item counts towards nothing until it is approved again."
        confirmLabel="Reopen"
        busyLabel="Reopening…"
        requireRationale
        onConfirm={(rationale) => evidenceApi.reopenItem(orgId, item.id, rationale)}
        onDone={onDone}
      />,
    );
  }
  if (item.status === "approved") {
    actions.push(
      <GovernedAction
        key="expire"
        permission={GOVERNANCE_PERMISSIONS.evidenceManage}
        label="Expire"
        title="Expire this evidence"
        description="Records that this evidence is no longer current."
        consequence="Expired evidence stops counting towards coverage immediately and appears in the missing list."
        confirmLabel="Expire"
        busyLabel="Expiring…"
        requireRationale
        destructive
        onConfirm={(rationale) => evidenceApi.expireItem(orgId, item.id, rationale)}
        onDone={onDone}
      />,
    );
  }
  if (
    (item.status === "approved" || item.status === "rejected" || item.status === "expired") &&
    replacements.length > 0
  ) {
    actions.push(
      <GovernedAction
        key="supersede"
        permission={GOVERNANCE_PERMISSIONS.evidenceManage}
        label="Supersede"
        title="Supersede this evidence"
        description="Records that another evidence item replaces this one."
        consequence="Superseding is final: the item is kept for the audit trail but cannot be revised again."
        confirmLabel="Supersede"
        busyLabel="Superseding…"
        requireRationale
        destructive
        onConfirm={(rationale) => evidenceApi.supersedeItem(orgId, item.id, replacement, rationale)}
        onDone={onDone}
      >
        <Field label="Replaced by">
          {(id) => (
            <Select id={id} value={replacement} onChange={(e) => setReplacementId(e.target.value)}>
              {replacements.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </GovernedAction>,
    );
  }
  return <>{actions}</>;
}

/**
 * Capturing content. A file-backed version names an existing Documents record —
 * Policy AI stores no bytes and this dialog uploads nothing, so a document that
 * has not been imported yet cannot be used.
 */
function AddVersion({
  orgId,
  item,
  onDone,
}: {
  orgId: string;
  item: EvidenceItem;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"document" | "url" | "attestation">("document");
  const [policyId, setPolicyId] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [attestationText, setAttestationText] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);

  const policies = useAsync(
    async () => (open ? (await policyApi.policies(orgId)).data.policies : []),
    [orgId, open],
  );
  const policy = policyId || policies.data?.[0]?.id || "";
  const documents = useAsync<PolicyDocument[]>(
    async () => (open && policy ? (await evidenceApi.policyDocuments(orgId, policy)).data.documents : []),
    [orgId, policy, open],
  );
  const document = documentId || documents.data?.[0]?.id || "";
  const selected = (documents.data ?? []).find((d) => d.id === document) ?? null;

  const ready =
    kind === "document" ? Boolean(document) : kind === "url" ? externalUrl.trim().length > 0 : attestationText.trim().length > 0;

  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.addVersion(orgId, item.id, {
        ...(kind === "document" ? { documentId: document } : {}),
        ...(kind === "url" ? { externalUrl: externalUrl.trim() } : {}),
        ...(kind === "attestation" ? { attestationText: attestationText.trim() } : {}),
      }),
    );
    if (!ok) return;
    setOpen(false);
    setExternalUrl("");
    setAttestationText("");
    onDone();
  };

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        disabled={item.status === "superseded"}
        onClick={() => setOpen(true)}
      >
        Add version
      </Button>
      <Dialog
        open={open}
        title="Capture an evidence version"
        description="Versions are immutable. Policy AI computes the content hash."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!ready || mutation.busy} onClick={() => void submit()}>
              {mutation.busy ? "Capturing…" : "Capture version"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Content">
          {(id) => (
            <Select
              id={id}
              value={kind}
              onChange={(e) => setKind(e.target.value as "document" | "url" | "attestation")}
            >
              <option value="document">Existing Documents record</option>
              <option value="url">External URL</option>
              <option value="attestation">Attestation</option>
            </Select>
          )}
        </Field>

        {kind === "document" ? (
          <>
            <p className="muted-note">
              Policy AI holds no files of its own. Pick a document that has already been imported into
              Documents. A standalone upload here is not possible, so import it from Studio Documents
              first if it is missing.
            </p>
            <Field label="Policy">
              {(id) => (
                <Select id={id} value={policy} onChange={(e) => setPolicyId(e.target.value)}>
                  {(policies.data ?? []).map((p: Policy) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Documents record">
              {(id) => (
                <Select id={id} value={document} onChange={(e) => setDocumentId(e.target.value)}>
                  {(documents.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.filename}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {selected ? (
              <dl className="detail-grid">
                <div>
                  <dt>Filename</dt>
                  <dd>{selected.filename}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{selected.mimeType}</dd>
                </div>
                <div>
                  <dt>Size</dt>
                  <dd>{Math.round(selected.byteSize / 1024)} KB</dd>
                </div>
                <div>
                  <dt>Pages</dt>
                  <dd>{selected.pageCount}</dd>
                </div>
                <div>
                  <dt>Document checksum</dt>
                  <dd>
                    <code>{selected.checksumSha256}</code>
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="muted-note">No Documents records for this policy yet.</p>
            )}
            <ImportStoredDocument
              orgId={orgId}
              policyId={policy}
              onImported={() => documents.reload()}
            />
          </>
        ) : null}

        {kind === "url" ? (
          <Field label="External URL" help="Where the evidence can be retrieved. The URL is hashed, not the page.">
            {(id) => (
              <TextInput id={id} value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} />
            )}
          </Field>
        ) : null}

        {kind === "attestation" ? (
          <Field label="Attestation" help="A statement of fact by a named person. Stored verbatim and hashed.">
            {(id) => (
              <TextArea
                id={id}
                rows={4}
                value={attestationText}
                onChange={(e) => setAttestationText(e.target.value)}
              />
            )}
          </Field>
        ) : null}
      </Dialog>
    </>
  );
}

/**
 * Brings a document the shared Documents Platform already stores into a policy,
 * by id. The bytes are never copied through the browser: Policy AI reads them
 * from Documents and records the platform's own extraction, so a scan that needs
 * OCR is refused here with that reason rather than imported as empty text.
 */
function ImportStoredDocument({
  orgId,
  policyId,
  onImported,
}: {
  orgId: string;
  policyId: string;
  onImported: () => void;
}) {
  const [documentId, setDocumentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const stored = useAsync(
    async () => (orgId ? (await documentsApi.list(orgId, { status: "active" })).documents : []),
    [orgId],
  );

  const submit = async () => {
    if (!documentId || !policyId) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await evidenceApi.importStoredDocument(orgId, policyId, { documentId });
      const failure = result.data.items.find((i) => i.failureReason !== null);
      setMessage(
        failure
          ? `Import refused: ${failure.failureReason}`
          : `Imported ${result.data.job.importedCount} document(s).`,
      );
      onImported();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ods-stack">
      <DocumentSelect
        documents={stored.data ?? []}
        value={documentId}
        onChange={setDocumentId}
        label="Import from Studio Documents"
      />
      <div className="badge-row">
        <Button
          variant="secondary"
          size="sm"
          disabled={!documentId || !policyId || busy}
          onClick={() => void submit()}
        >
          {busy ? "Importing…" : "Import into this policy"}
        </Button>
        {message ? <span className="muted-note">{message}</span> : null}
      </div>
    </div>
  );
}

/**
 * Link picker. Requirements can be chosen from the adopted frameworks; the other
 * target types are identified by id, because their own screens land in later
 * phases and inventing a picker for them now would be guesswork.
 */
function AddLink({
  orgId,
  itemId,
  onDone,
}: {
  orgId: string;
  itemId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [targetType, setTargetType] = useState<EvidenceLinkTarget>("requirement");
  const [linkRole, setLinkRole] = useState<EvidenceLinkRole>("supports");
  const [versionId, setVersionId] = useState("");
  const [requirementId, setRequirementId] = useState("");
  const [targetId, setTargetId] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);

  const versions = useAsync<{ version: FrameworkVersion; framework: ComplianceFramework }[]>(
    async () => {
      if (!open || targetType !== "requirement") return [];
      const frameworks = (await frameworksApi.frameworks(orgId, { scope: "tenant" })).data.frameworks;
      return (
        await Promise.all(
          frameworks.map(async (framework) =>
            (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
              version,
              framework,
            })),
          ),
        )
      ).flat();
    },
    [orgId, open, targetType],
  );
  const version = versionId || versions.data?.[0]?.version.id || "";
  const requirements = useAsync<FrameworkRequirement[]>(
    async () =>
      open && targetType === "requirement" && version
        ? (await frameworksApi.requirements(orgId, version)).data.requirements
        : [],
    [orgId, version, open, targetType],
  );
  const requirement = requirementId || requirements.data?.[0]?.id || "";

  const target = targetType === "requirement" ? requirement : targetId.trim();

  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.addLink(orgId, itemId, { targetType, targetId: target, linkRole }),
    );
    if (!ok) return;
    setOpen(false);
    setTargetId("");
    onDone();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        Link target
      </Button>
      <Dialog
        open={open}
        title="Link this evidence"
        description="Says what this evidence is about, and whether it supports or refutes it."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!target || mutation.busy} onClick={() => void submit()}>
              {mutation.busy ? "Linking…" : "Add link"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Target type">
          {(id) => (
            <Select
              id={id}
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as EvidenceLinkTarget)}
            >
              {EVIDENCE_LINK_TARGETS.map((t) => (
                <option key={t} value={t}>
                  {targetLabel(t)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {targetType === "requirement" ? (
          <>
            <Field label="Framework version">
              {(id) => (
                <Select id={id} value={version} onChange={(e) => setVersionId(e.target.value)}>
                  {(versions.data ?? []).map(({ version: v, framework }) => (
                    <option key={v.id} value={v.id}>
                      {framework.name} {v.versionLabel}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Requirement">
              {(id) => (
                <Select id={id} value={requirement} onChange={(e) => setRequirementId(e.target.value)}>
                  {(requirements.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.requirementKey}: {r.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </>
        ) : (
          <Field
            label={`${targetLabel(targetType)} id`}
            help="Screens for this target type land in a later phase; paste its id for now."
          >
            {(id) => <TextInput id={id} value={targetId} onChange={(e) => setTargetId(e.target.value)} />}
          </Field>
        )}

        <Field
          label="Role"
          help="Refuting evidence is kept, not hidden. A target with both supporting and refuting evidence is reported as conflicting."
        >
          {(id) => (
            <Select
              id={id}
              value={linkRole}
              onChange={(e) => setLinkRole(e.target.value as EvidenceLinkRole)}
            >
              {EVIDENCE_LINK_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Dialog>
    </>
  );
}
